import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { mockWebinars } from '../../data/webinarData';
import { mockMemorials } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import AddSeriesModal from '../Webinars/AddSeriesModal';
import EventAttendanceCheckIn from '../../features/events/EventAttendanceCheckIn';

const {
  FiCalendar, FiClock, FiVideo, FiMapPin, FiUsers,
  FiCheckCircle, FiStar, FiEdit, FiArrowRight, FiAlertCircle,
  FiRadio, FiBookOpen, FiPlayCircle, FiBookmark, FiPlus,
} = FiIcons;

const STORAGE_KEY = 'ofl_registered_webinars';

/* ── helpers ── */
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const StatusPill = ({ label, color }) => (
  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${color}`}>
    {label}
  </span>
);

/* ── Section wrapper ── */
const Section = ({ icon, title, badge, children, empty }) => (
  <div className="bg-white rounded-[32px] border border-brand-dark shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-8 py-6 border-b border-brand-light">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-accent-gold/10 text-accent-gold rounded-xl flex items-center justify-center">
          <SafeIcon icon={icon} className="w-5 h-5" />
        </div>
        <h3 className="text-xl font-serif text-accent-navy">{title}</h3>
      </div>
      {badge !== undefined && (
        <span className="px-3 py-1 bg-accent-navy text-white text-[9px] font-black uppercase tracking-widest rounded-full">{badge}</span>
      )}
    </div>
    <div className="p-6">
      {empty ? (
        <div className="py-10 text-center text-text-muted">
          <SafeIcon icon={FiAlertCircle} className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-light italic">{empty}</p>
        </div>
      ) : children}
    </div>
  </div>
);

/* ── Webinar card ── */
const WebinarCard = ({ webinar, badge, navigate }) => {
  const isFree = webinar.monetization?.isFree !== false;
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="flex flex-col sm:flex-row gap-4 p-5 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold transition-all group">
      <div className="w-full sm:w-24 h-20 sm:h-full rounded-xl overflow-hidden shrink-0">
        <img src={webinar.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="flex-grow min-w-0">
        <div className="flex flex-wrap gap-2 mb-2">
          {badge}
          {webinar.isLive && <StatusPill label="Live Now" color="bg-red-50 text-red-600 border-red-200" />}
          <StatusPill
            label={isFree ? 'Free' : `$${webinar.monetization.price}`}
            color={isFree ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
          />
        </div>
        <h4 className="font-serif text-accent-navy text-base leading-tight mb-2 line-clamp-1">{webinar.title}</h4>
        <div className="flex flex-wrap gap-3 text-[10px] font-black uppercase tracking-widest text-text-muted">
          <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} className="text-accent-gold" />{webinar.date}</span>
          <span className="flex items-center gap-1"><SafeIcon icon={FiClock} className="text-accent-gold" />{webinar.time}</span>
          <span className="flex items-center gap-1"><SafeIcon icon={FiUsers} className="text-accent-gold" />{webinar.registrants} registered</span>
        </div>
      </div>
      <div className="flex items-center shrink-0">
        <button
          onClick={() => navigate(`/webinar/${webinar.id}`)}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all"
        >
          Enter <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

/* ── Memorial event card ── */
const MemorialEventCard = ({ event, memorial }) => (
  <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="flex flex-col sm:flex-row gap-4 p-5 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold transition-all">
    <div className="flex-grow min-w-0">
      <div className="flex flex-wrap gap-2 mb-2">
        <StatusPill label={event.type} color="bg-accent-navy/10 text-accent-navy border-accent-navy/20" />
      </div>
      <h4 className="font-serif text-accent-navy text-base leading-tight mb-1 line-clamp-1">{event.title}</h4>
      <p className="text-xs text-text-muted font-light mb-3 line-clamp-1">
        In memory of <span className="font-bold text-accent-navy">{memorial.name}</span>
      </p>
      <div className="flex flex-wrap gap-3 text-[10px] font-black uppercase tracking-widest text-text-muted">
        <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} className="text-accent-gold" />{formatDate(event.date)}</span>
        {formatTime(event.date) && (
          <span className="flex items-center gap-1"><SafeIcon icon={FiClock} className="text-accent-gold" />{formatTime(event.date)}</span>
        )}
        {event.location && (
          <span className="flex items-center gap-1"><SafeIcon icon={FiMapPin} className="text-accent-gold" />{event.location}</span>
        )}
      </div>
    </div>
  </motion.div>
);

/* ── In-person event with collapsible attendance roster ── */
const InPersonEventItem = ({ event, memorial }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <MemorialEventCard event={event} memorial={memorial} />
      <div className="mt-2 text-right">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="inline-flex items-center gap-2 text-accent-gold text-[10px] font-black uppercase tracking-widest hover:text-accent-navy transition-colors"
        >
          <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" />
          {open ? 'Hide Attendance' : 'Manage Attendance'}
        </button>
      </div>
      {open && <EventAttendanceCheckIn event={event} memorial={memorial} />}
    </div>
  );
};

/* ── Main component ── */
const MyEventsTab = () => {
  const { user, permissions } = useAuth();
  const navigate  = useNavigate();
  const canHost   = permissions?.canHostWebinar?.() || false;

  const [subTab, setSubTab]               = useState('registered');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [localCreated, setLocalCreated]   = useState([]);

  /* Registered IDs from localStorage */
  const registeredIds = useMemo(() => JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'), []);

  /* Data buckets */
  const registeredWebinars = useMemo(() => mockWebinars.filter(w => registeredIds.includes(w.id)), [registeredIds]);
  const createdWebinars    = useMemo(() => canHost ? [...localCreated, ...mockWebinars.filter((_, i) => i < 2)] : [], [canHost, localCreated]);
  const speakingWebinars   = useMemo(() =>
    mockWebinars.filter(w =>
      user?.name && w.speaker?.toLowerCase().includes(user.name.split(' ')[0]?.toLowerCase())
    ), [user]);
  const memorialEvents     = useMemo(() =>
    mockMemorials.flatMap(m => (m.events || []).map(ev => ({ event: ev, memorial: m }))), []);
  const attendedWebinars   = useMemo(() => mockWebinars.filter((w, i) => i > 2 && !w.isLive && w.monetization?.isFree), []);
  const watchLaterWebinars = useMemo(() => mockWebinars.filter((_, i) => i === 1 || i === 3), []);
  const recordedWebinars   = useMemo(() => mockWebinars.filter(w => !w.isLive), []);
  const liveWebinars       = useMemo(() => mockWebinars.filter(w => w.isLive), []);
  const inPersonEvents     = useMemo(() =>
    mockMemorials.flatMap(m =>
      (m.events || []).filter(e => e.location && !e.isVirtual).map(ev => ({ event: ev, memorial: m }))
    ), []);

  const handleWebinarSaved = (newWebinar) => {
    setLocalCreated(prev => [newWebinar, ...prev]);
    setIsCreateModalOpen(false);
  };

  const SUB_TABS = [
    { id: 'registered', label: 'Registered',     icon: FiCheckCircle, count: registeredWebinars.length },
    { id: 'rsvp',       label: "RSVP'd Events",  icon: FiCalendar,    count: memorialEvents.length },
    { id: 'created',    label: 'Created',         icon: FiEdit,        count: createdWebinars.length, hidden: !canHost },
    { id: 'speaking',   label: 'Speaking',        icon: FiStar,        count: speakingWebinars.length },
    { id: 'attended',   label: 'Attended',        icon: FiCheckCircle, count: attendedWebinars.length },
    { id: 'watchLater', label: 'Watch Later',     icon: FiBookmark,    count: watchLaterWebinars.length },
    { id: 'recorded',   label: 'Recorded',        icon: FiPlayCircle,  count: recordedWebinars.length },
    { id: 'live',       label: 'Live',            icon: FiRadio,       count: liveWebinars.length },
    { id: 'inPerson',   label: 'In-Person',       icon: FiMapPin,      count: inPersonEvents.length },
  ].filter(t => !t.hidden);

  return (
    <div className="space-y-8">
      {/* ── Header + sub-tabs ── */}
      <div className="bg-white rounded-[32px] p-8 border border-brand-dark shadow-sm">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-accent-navy to-[#1a2a5e] rounded-[20px] flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiRadio} className="w-7 h-7 text-accent-gold" />
            </div>
            <div>
              <h2 className="text-2xl font-serif text-accent-navy">My Events</h2>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                Webinars · Memorial Events · Sessions
              </p>
            </div>
          </div>

          {/* Create Webinar CTA — visible only to eligible roles */}
          {canHost && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy transition-all shadow-lg shadow-accent-gold/20"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" /> New Webinar
            </button>
          )}
        </div>

        {/* Sub-tab pills */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
          {SUB_TABS.map(t => (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border-2 shrink-0 ${
                subTab === t.id
                  ? 'bg-accent-navy border-accent-navy text-white shadow-lg'
                  : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
              }`}
            >
              <SafeIcon icon={t.icon} className="w-3 h-3" />
              {t.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-black ${subTab === t.id ? 'bg-white/20 text-white' : 'bg-brand-light text-text-muted'}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Content panels ── */}
      <AnimatePresence mode="wait">

        {subTab === 'registered' && (
          <motion.div key="registered" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiCheckCircle} title="Registered Webinars" badge={registeredWebinars.length}
              empty={registeredWebinars.length === 0 ? "You haven't registered for any webinars yet." : null}>
              <div className="space-y-4">
                {registeredWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Registered" color="bg-green-50 text-green-700 border-green-200" />} />
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-brand-light text-center">
                <button onClick={() => navigate('/webinars')}
                  className="inline-flex items-center gap-2 text-accent-gold text-[10px] font-black uppercase tracking-widest hover:text-accent-navy transition-colors">
                  <SafeIcon icon={FiBookOpen} className="w-3.5 h-3.5" /> Browse More Webinars
                </button>
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'rsvp' && (
          <motion.div key="rsvp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiCalendar} title="RSVP'd Memorial Events" badge={memorialEvents.length}
              empty={memorialEvents.length === 0 ? 'No upcoming memorial events to display.' : null}>
              <div className="space-y-4">
                {memorialEvents.map(({ event, memorial }) => (
                  <MemorialEventCard key={event.id} event={event} memorial={memorial} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'created' && (
          <motion.div key="created" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiEdit} title="Webinars You've Created" badge={createdWebinars.length}
              empty={createdWebinars.length === 0 ? "You haven't created any webinars yet." : null}>
              <div className="space-y-4">
                {createdWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Host" color="bg-purple-50 text-purple-700 border-purple-200" />} />
                ))}
              </div>
              {/* ── Create New Webinar → opens modal directly ── */}
              <div className="mt-6 pt-4 border-t border-brand-light text-center">
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 text-accent-gold text-[10px] font-black uppercase tracking-widest hover:text-accent-navy transition-colors"
                >
                  <SafeIcon icon={FiVideo} className="w-3.5 h-3.5" /> Create New Webinar
                </button>
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'speaking' && (
          <motion.div key="speaking" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiStar} title="Speaking Engagements" badge={speakingWebinars.length}
              empty={speakingWebinars.length === 0 ? "No speaking engagements found. When you're listed as a speaker, sessions will appear here." : null}>
              <div className="space-y-4">
                {speakingWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Speaker" color="bg-accent-gold/10 text-accent-gold border-accent-gold/30" />} />
                ))}
              </div>
              {/* Speakers who can host can also create directly from here */}
              {canHost && (
                <div className="mt-6 pt-4 border-t border-brand-light text-center">
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center gap-2 text-accent-gold text-[10px] font-black uppercase tracking-widest hover:text-accent-navy transition-colors"
                  >
                    <SafeIcon icon={FiVideo} className="w-3.5 h-3.5" /> Propose a New Session
                  </button>
                </div>
              )}
            </Section>
          </motion.div>
        )}

        {subTab === 'attended' && (
          <motion.div key="attended" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiCheckCircle} title="Attended Sessions" badge={attendedWebinars.length}
              empty={attendedWebinars.length === 0 ? 'No attended sessions history found.' : null}>
              <div className="space-y-4">
                {attendedWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Attended" color="bg-brand-dark/50 text-accent-navy border-brand-dark" />} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'watchLater' && (
          <motion.div key="watchLater" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiBookmark} title="Watch Later" badge={watchLaterWebinars.length}
              empty={watchLaterWebinars.length === 0 ? 'No sessions saved for later.' : null}>
              <div className="space-y-4">
                {watchLaterWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Saved" color="bg-blue-50 text-blue-700 border-blue-200" />} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'recorded' && (
          <motion.div key="recorded" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiPlayCircle} title="Recorded Sessions" badge={recordedWebinars.length}
              empty={recordedWebinars.length === 0 ? 'No recorded sessions available.' : null}>
              <div className="space-y-4">
                {recordedWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Recorded" color="bg-indigo-50 text-indigo-700 border-indigo-200" />} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'live' && (
          <motion.div key="live" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiRadio} title="Live Sessions" badge={liveWebinars.length}
              empty={liveWebinars.length === 0 ? 'No live sessions happening right now.' : null}>
              <div className="space-y-4">
                {liveWebinars.map(w => (
                  <WebinarCard key={w.id} webinar={w} navigate={navigate}
                    badge={<StatusPill label="Live Now" color="bg-red-50 text-red-600 border-red-200" />} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

        {subTab === 'inPerson' && (
          <motion.div key="inPerson" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Section icon={FiMapPin} title="In-Person Events" badge={inPersonEvents.length}
              empty={inPersonEvents.length === 0 ? 'No in-person events found.' : null}>
              <div className="space-y-4">
                {inPersonEvents.map(({ event, memorial }) => (
                  <InPersonEventItem key={event.id} event={event} memorial={memorial} />
                ))}
              </div>
            </Section>
          </motion.div>
        )}

      </AnimatePresence>

      {/* ── New Webinar Series Modal ── */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <AddSeriesModal
            onClose={() => setIsCreateModalOpen(false)}
            onSave={handleWebinarSaved}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyEventsTab;